import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { bookingApi } from '../../api/booking';
import { schedulingApi } from '../../api/scheduling';
import { queryKeys } from '../queryClient';
import type { BookingListFilters, CreateBookingRequest, QuoteRequest } from '../../types/domain';

/** Catalog is public and rarely changes — cache it for an hour. */
export function useCatalog() {
  return useQuery({
    queryKey: queryKeys.catalog,
    queryFn: () => bookingApi.getCatalog(),
    staleTime: 60 * 60_000,
  });
}

export function useCustomerBookings(customerId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.bookings(customerId),
    queryFn: () => bookingApi.getCustomerBookings(customerId!),
    enabled: !!customerId,
    staleTime: 60_000,
  });
}

/** Quote is a mutation (it creates a server-side Quote row) — not cached. */
export function useCreateQuote() {
  return useMutation({ mutationFn: (body: QuoteRequest) => bookingApi.createQuote(body) });
}

export function useCreateBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBookingRequest) => bookingApi.createBooking(body),
    onSuccess: (_res, vars) => {
      qc.invalidateQueries({ queryKey: queryKeys.bookings(vars.customerId) });
      qc.invalidateQueries({ queryKey: ['schedule'] });
    },
  });
}

export function useCancelBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) => bookingApi.cancelBooking(id, reason),
    onSuccess: (_res, vars) => {
      // 'bookings' (plural, useCustomerBookings) and 'booking' (singular, useBooking) are
      // separate query-key namespaces — both need invalidating after a cancel.
      qc.invalidateQueries({ queryKey: ['bookings'] });
      qc.invalidateQueries({ queryKey: queryKeys.booking(vars.id) });
    },
  });
}

/** Admin-wide bookings list (manager+), newest first, cursor-paginated. */
export function useBookingsList(filters: BookingListFilters = {}) {
  return useQuery({
    queryKey: ['bookings', 'list', filters],
    queryFn: () => bookingApi.listBookings(filters),
  });
}

/** Reschedule one visit within a booking, without touching the rest of a recurring series. */
export function useRescheduleBookingJob(bookingId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, newStart, reason }: { jobId: string; newStart: string; reason: string }) =>
      schedulingApi.reschedule(jobId, newStart, reason),
    onSuccess: () => {
      if (bookingId) qc.invalidateQueries({ queryKey: queryKeys.booking(bookingId) });
    },
  });
}

/** Cancel a single visit within a booking, without cancelling the rest of a recurring series. */
export function useCancelBookingJob(bookingId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, reason }: { jobId: string; reason: string }) => schedulingApi.cancelJob(jobId, reason),
    onSuccess: () => {
      if (bookingId) qc.invalidateQueries({ queryKey: queryKeys.booking(bookingId) });
    },
  });
}

export function useBooking(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.booking(id ?? ''),
    queryFn: () => bookingApi.getBooking(id!),
    enabled: !!id,
  });
}

export function useBookingHistory(id: string | undefined, cursor?: string) {
  return useQuery({
    queryKey: queryKeys.bookingHistory(id ?? '', cursor),
    queryFn: () => bookingApi.getBookingHistory(id!, cursor),
    enabled: !!id,
  });
}

/** Admin quotes list — most recent 100, newest first. */
export function useQuotes() {
  return useQuery({ queryKey: queryKeys.quotes, queryFn: () => bookingApi.listQuotes() });
}

export function useQuote(id: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.quotes, id],
    queryFn: () => bookingApi.getQuote(id!),
    enabled: !!id,
  });
}
